import React, {useState} from "react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from '@tanstack/react-query';
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";
import AppleSelect from "../Common/AppleSelect";

const UNIT_CODES = [
    {value: 'pc', label: 'pc - Pièces'},
    {value: 'kg', label: 'kg - Kilogramme'},
    {value: 'g', label: 'g - Gramme'},
    {value: 'mg', label: 'mg - Milligramme'},
    {value: 'l', label: 'l - Litre'},
    {value: 'ml', label: 'ml - Millilitre'},
    {value: 'cl', label: 'cl - Centilitre'},
    {value: 'm', label: 'm - Mètre'},
    {value: 'cm', label: 'cm - Centimètre'},
    {value: 'mm', label: 'mm - Millimètre'},
    {value: 'u', label: 'u - Unité'},
    {value: 'box', label: 'box - Boîte'},
    {value: 'pack', label: 'pack - Paquet'},
];

const UnitEditor = ({unit, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: unit ? unit.name : '',
        code: unit ? unit.code : '',
        value: unit ? unit.value : '1',
        active: unit ? unit.active : true
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const queryClient = useQueryClient();

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom de l'unité est requis";
        }

        if (!formData.code.trim()) {
            newErrors.code = "Le code est requis";
        }

        if (!formData.value.trim()) {
            newErrors.value = "La valeur est requise";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const payload = {
                name: formData.name,
                code: formData.code,
                value: formData.value,
                active: formData.active ? true : false
            };

            if (unit) {
                // Update existing unit
                await api.put(`/units/${unit.id}`, payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Unité mise à jour avec succès');
            } else {
                // Create new unit
                await api.post('/units', payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Unité créée avec succès');
            }

            queryClient.invalidateQueries({queryKey: ['units']});
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={unit ? 'Modifier l\'unité' : 'Nouvelle unité'}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="unit-form" saving={submitting}
                                 label={unit ? 'Enregistrer' : 'Créer'}/>}
        >
            <form id="unit-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="Ex: Kilogramme"
                        disabled={submitting}
                    />
                </FormField>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField id="code" label="Code" required error={errors.code}>
                        <AppleSelect
                            id="code"
                            name="code"
                            value={formData.code}
                            onChange={handleChange}
                            error={errors.code}
                            disabled={submitting}
                            placeholder="Sélectionner un code"
                            options={UNIT_CODES}
                        />
                    </FormField>

                    <FormField id="value" label="Valeur" required error={errors.value}>
                        <input
                            type="text"
                            inputMode="decimal"
                            id="value"
                            name="value"
                            value={formData.value}
                            onChange={handleChange}
                            className={inputClass(errors.value)}
                            placeholder="Ex: 1"
                            disabled={submitting}
                        />
                    </FormField>
                </div>

                <SwitchRow id="active" label="Unité active" checked={formData.active} disabled={submitting}
                           onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
            </form>
        </Modal>
    );
};

export default UnitEditor;

